import React, { useEffect, useState, useRef, useLayoutEffect, forwardRef } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import "./viewdetails.css"; // CSS file for styling
import { formatToDecimal } from "../controls/conversions";
import { formatToFractional } from '../controls/conversions';
import DynamicTemplateRenderer from "./DynamicTemplateRendering";
import CompanyHeader from "./company_header";
import Watermark from "../components/Watermark";
import { companyName } from "../configs";
import { INITX, SCALE, FRAME_WIDTH, GAP } from "../constants";
import { calculatePanelDimensions } from "../lib/displaySizes";

// ▶ [VP-AUTOFIT 2026-05-27] Auto-Zoom-Out target: the Line Item / Price table must
//   occupy at least this fraction of the ViewDetails panel width. Tune this single
//   number to make the drawing smaller / table larger (or vice versa).
//   Examples: 0.40 → drawing wider, table narrower
//             0.45 → ~current preference
//             0.50 / 0.55 → table dominates, drawing more compact
//   ROLLBACK: `grep -rn "VP-AUTOFIT" bbox/src` finds every line of this fix
//   (viewdetails.js, DynamicTemplateRenderer.css, windows/wint/ww.js).
const MIN_TABLE_PCT = 0.45;

//old line - changed to forwardRef - to get toPdf working from outside
//const ViewDetails = ({ isVisible, data, doorData = null, drawing = null, drawTopView = null, updateCalcData }) => {
const ViewDetails = forwardRef((props, ref) => {
  const {
    isVisible,
    data,
    doorData,
    drawing,
    drawTopView,
    updateCalcData,
    zoom = 1.0, // Default zoom to 1.0 (100%)
    onPriceInvisible // optional: parent's Zoom Out handler. When passed (only ww.js does)
                     // we loop calling it until no Price cell is being ellipsis-clipped.
  } = props;
    const [pricingData, setPricingData] = useState(null);
    const panelRef = useRef(null);

    // [VP-AUTOFIT 2026-05-27]
    // ONE-SHOT auto-fit: re-arm only when the panel becomes visible (initial open
    // or re-open). After the criterion is satisfied once, the gate flips off and
    // the user is free to Zoom In / Zoom Out manually without the effect fighting
    // back. Re-opening the panel re-arms it for the next session.
    const needsFitRef = useRef(true);
    useEffect(() => {
      if (isVisible) needsFitRef.current = true;
    }, [isVisible]);

    // [VP-AUTOFIT 2026-05-27]
    // CRITERION: while the gate is armed, if the price-table currently occupies
    //   less than MIN_TABLE_PCT of the panel width — i.e. the drawing is too
    //   dominant and the table is squeezed below the desired proportion — call
    //   onPriceInvisible() (the parent's handleZoomOut, clamped to 0.2). The
    //   natural re-render re-runs this check; the loop converges and then flips
    //   the gate off so subsequent renders (e.g. from a user's manual Zoom In)
    //   are NOT auto-corrected.
    useLayoutEffect(() => {
      if (!isVisible || !needsFitRef.current) return;
      const panel = panelRef.current;
      if (!panel || typeof onPriceInvisible !== 'function') return;
      const table = panel.querySelector('.price-table');
      if (!table) return;
      const panelWidth = panel.getBoundingClientRect().width;
      const tableWidth = table.getBoundingClientRect().width;
      if (tableWidth < panelWidth * MIN_TABLE_PCT) {
        onPriceInvisible();           // still violating — one more step toward fit
      } else {
        needsFitRef.current = false;  // converged — user is in control from here
      }
    });
    //alert(JSON.stringify(data, null, 2));

    const handleDownloadPDF = async () => {
        const element = document.querySelector(".view-details");
        if (!element) {
            console.error("Element not found!");
            return;
        }

        // Capture the element with better resolution
        const canvas = await html2canvas(element, {
            scale: 1, // 📌 Higher scale = better quality
            useCORS: true,
        });

        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let position = 0;

        // 🧾 Multi-page handling if needed
        if (imgHeight < pageHeight) {
            pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
        } else {
            // Split into pages
            while (position < imgHeight) {
            pdf.addImage(imgData, "PNG", 0, position ? 0 : position, imgWidth, imgHeight);
            position += pageHeight;
            if (position < imgHeight) pdf.addPage();
            }
        }
        const today = new Date().toISOString().split('T')[0];
        pdf.save(`${doorData?.frameData?.selectedDoor?.name || "No name available"}-${today}.pdf`);
    };

    function getTransomSize()
    {
        if (!doorData?.frameData?.selectedDoor) return null;
        const width = getSize().width;
        const h = formatToDecimal(getSize().height);
        const tw = width;
        let th_formatted = getSize().height;
        const calculated_h = parseFloat(doorData.transomData.OverallHeight) - parseFloat(h);
        if (calculated_h > 0){
            th_formatted = formatToFractional(calculated_h);
        }
        else {th_formatted = 0};
        let transomSize = {};
        transomSize.width = width;
        transomSize.height = th_formatted;
        transomSize.h = calculated_h;
        transomSize.w = getSize().w;
        return transomSize;
    }
    function getSize()
    {
        if (!doorData?.frameData?.selectedDoor) return null;
        const selectedDoor = doorData?.frameData?.selectedDoor;
        let width, height;
        if ([1, 5].includes(selectedDoor.id)) {
            width  = doorData.frameData.frameWidth;
            height = doorData.frameData.frameHeight;
        } 
        if ([2, 3, 6, 7].includes(selectedDoor.id)) {
            width  = parseInt(doorData.frameData.frameWidth) + parseInt(doorData.frameData.sideLiteSize);
            height = doorData.frameData.frameHeight;
        } 
        if ([4, 8].includes(selectedDoor.id)) {
            width  = parseInt(doorData.frameData.frameWidth) + 2* parseInt(doorData.frameData.sideLiteSize);
            height = doorData.frameData.frameHeight;
        }
        let size = {};
        size.w = width;
        size.h = height;
        size.width = formatToFractional(width);
        size.height = formatToFractional(height);
        return size;
    }
    function getFrameSize()
    {
        if (!doorData?.frameData?.selectedDoor) return null;
        const selectedDoor = doorData?.frameData?.selectedDoor;
        let width, height;
        const sideLiteSize = doorData?.frameData?.sideLiteSize || 0;
        
        if ([1, 5].includes(selectedDoor.id)) {
            width  = doorData.frameData.frameWidth;
            height = doorData.frameData.frameHeight;
        } 
        if ([2, 3, 6, 7].includes(selectedDoor.id)) {
            width  = parseFloat(doorData.frameData.frameWidth) + parseFloat(sideLiteSize);
            height = doorData.frameData.frameHeight;
        } 
        if ([4, 8].includes(selectedDoor.id)) {
            width  = parseFloat(doorData.frameData.frameWidth) + 2* parseFloat(sideLiteSize);
            height = doorData.frameData.frameHeight;
        }
        return "Frame Size (tip to tip): " + formatToFractional(width) + " x " + formatToFractional(height);
    }

//==>
    let dynamicProps = {
        ...doorData,
      };
      
      // Entry doors → frame + transom
      if (doorData.quoteTemplate === "euro_entry_door" || doorData.quoteTemplate === "patiodoor_template") {
        const frameSize = getFrameSize();
        const transomSize = getTransomSize();
        const { height: transomHeight, width: transomWidth, w: transomW, h: transomH } = transomSize;
      
        dynamicProps.frameData = {
          ...doorData.frameData,
          frameSize,
        };
      
        dynamicProps.transomData = {
          ...doorData.transomData,
          transomSize,
          transomHeight,
          transomWidth,
          transomW,
          transomH,
        };
      }
      
     // Patio doors → frame only
    if (doorData.quoteTemplate === "pleated_screen_template") {
        const frameSize = getFrameSize();
      
        dynamicProps.frameData = {
          ...doorData.frameData,
          frameSize,
        };
        dynamicProps.calc = {
            ...doorData.calc,
        };
    }
    let negativeOffsetX = 0;
    if (doorData?.frameData?.adjustView === true){
        if (doorData?.frameData?.frameWidth > 60){
            negativeOffsetX = -1*(doorData?.frameData?.frameWidth - 100);
        }  
    }
      // Pleated screen → no additions (just doorData)
      // No extra block needed unless you want to handle it explicitly
//==>
    let currentView = "";
    if (doorData.document.type === 'window' || doorData.document.type === 'patiodoors'){
        currentView = doorData?.flipped ? " - outside view" : " - inside view";
    }
    return (
        <div ref={panelRef} className={`view-details ${isVisible ? "visible" : ""}`}>
                    <CompanyHeader 
                        documentType="Quote" 
                        quoteData={doorData?.document} 
                        companyName={companyName}
                        
                    />
            <Watermark show={true} horizontalOffset={500}/>
            <h3>📌 {doorData?.frameData?.selectedDoor?.name + currentView || 
                     doorData?.frameData?.name + currentView || "No name available"}
                     <p></p>
            </h3>

            <div className="layout-container" style={{ display1:  'inline-block', border: '0px solid lightgreen',width: '100%'  }}> {/* PURPLE = layout-container */}
                <div className="image-section" style={{ border: '0px solid red' }}> {/* RED = image-section (outer wrapper) */}
                    <div className="scaled-image" style={{ border: '0px solid orange' }}>{drawing}{drawTopView}
                        <div>&nbsp;<div>&nbsp;
                        </div></div>
                    </div>
                    {(doorData?.notes?.LocationTag || doorData?.notes?.locationTag || doorData?.notes?.notes) && (() => {
                        let w;
                        if (doorData?.document?.type === 'patiodoors') {
                            w = (parseFloat(doorData?.frameData?.unitWidth_d) || 72) * SCALE;
                        } else if (doorData?.document?.type === 'pleatedscreens') {
                            w = (parseFloat(doorData?.frameData?.frameWidth) || 40) * SCALE;
                        } else {
                            const nSections = doorData?.frameData?.nSections || 1;
                            ({ w } = calculatePanelDimensions(nSections, doorData, FRAME_WIDTH, GAP));
                        }
                        const boxWidth = (w + INITX - 50) * zoom;
                        const locationTag = doorData?.notes?.LocationTag || doorData?.notes?.locationTag;
                        return (
                            <div className="customer-box" style={{ width: boxWidth, marginLeft: 10 }}>
                                {locationTag && (
                                <p><strong>Location:</strong> {locationTag}</p>
                                )}
                                {doorData?.notes?.notes && (
                                <p><strong>Notes:</strong> {doorData.notes.notes}</p>
                                )}
                            </div>
                        );
                    })()}
                </div>
                <div className="text-section" style={{ display1: 'inline-block', width: `calc(100% + ${70 * zoom}px)`,
                     position: 'relative',   border: '0px solid blue' , left: `-${70 * zoom}px`, marginRight: `-${70 * zoom}px` }}>
                    {/*<h4>System</h4>*/}
                    <DynamicTemplateRenderer
                        doorData={dynamicProps}
                        displayPrices={doorData.displayPrices}
                        updateCalcData={updateCalcData} // Pass the callback
                    />
                </div>
            </div>

            <ul>
                {pricingData
                    ? pricingData.map((item, index) => (
                          <li key={index}>
                              {item.name}: ${item.price.toFixed(2)}
                          </li>
                      ))
                    : data.map((control) => (
                          <li key={control.id}>
                              {control.id}: {control.value}
                          </li>
                      ))}
            </ul>

            {/* Debug: doorData dump */}
            {false && (
            <pre style={{
                backgroundColor: '#f5f5f5',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '11px',
                overflow: 'auto',
                maxHeight: '500px'
            }}>
                {JSON.stringify(doorData, null, 2)}
            </pre>
            )}
        </div>
    );
});

export default ViewDetails;